import { FilterTypes } from "../../../constants/filterTypes";
import { UserDto } from "../../../types/dto";
import { dodgySearch, formatUserNameWithEmailString } from "../../../utils";
import { DateOperators } from "../components/filters/dateColumnFilter";
import { NumberOperators } from "../components/filters/numberColumnFilter";

interface Row {
	values: Record<string, any>;
}

type FilterFn = (rows: Row[], id: string | string[], filterValue: any) => Row[];

const dateFilter: FilterFn = (rows, id, filterValue) =>
	rows.filter((row) => {
		try {
			const rowValue = row.values[id as string];

			const hasIsEmpty = filterValue.hasOwnProperty(
				DateOperators.isEmpty,
			);
			const hasIsExpired = filterValue.hasOwnProperty(
				DateOperators.isExpired,
			);
			const hasBefore = filterValue.hasOwnProperty(DateOperators.before);
			const hasAfter = filterValue.hasOwnProperty(DateOperators.after);

			if (hasIsEmpty && filterValue[DateOperators.isEmpty] === true) {
				return rowValue == null;
			}
			if (hasIsEmpty && filterValue[DateOperators.isEmpty] === false) {
				if (rowValue == null) return false;
			}

			if (rowValue == null) return false;

			const dateValue = new Date(rowValue);
			if (isNaN(dateValue.getTime())) return false;

			if (hasIsExpired) {
				const now = new Date();
				const isExpired = dateValue < now;
				if (filterValue[DateOperators.isExpired] !== isExpired)
					return false;
			}

			if (hasBefore) {
				if (dateValue >= new Date(filterValue[DateOperators.before]))
					return false;
			}

			if (hasAfter) {
				if (dateValue < new Date(filterValue[DateOperators.after]))
					return false;
			}

			return true;
		} catch (e) {
			console.error(e);
			return false;
		}
	});

export const createFilterTypes = (): Record<string, FilterFn> => ({
	[FilterTypes.LICENCES]: (rows, id, filterValue: string) =>
		rows.filter((row) => {
			try {
				const rowValue = row.values[id as string] as Array<{
					subscription?: { name: string };
					product?: { name: string };
					state?: { name: string };
				}>;

				for (const lic of rowValue) {
					if (
						dodgySearch(lic.subscription?.name, filterValue) ||
						dodgySearch(lic.product?.name, filterValue) ||
						dodgySearch(lic.state?.name, filterValue)
					) {
						return true;
					}
				}

				return false;
			} catch (e) {
				console.error(e);
				return false;
			}
		}),
	[FilterTypes.HTML]: (rows, id, filterValue: string) =>
		rows.filter((row) => {
			try {
				const rowValue = row.values[id as string];
				const formattedValue = JSON.stringify(rowValue);
				return dodgySearch(formattedValue, filterValue);
			} catch (e) {
				console.error(e);
				return false;
			}
		}),
	[FilterTypes.BOOLEAN]: (rows, id, filterValue: boolean | undefined) => {
		if (filterValue === undefined) {
			return rows;
		}
		return rows.filter((row) => {
			const rowValue = row.values[id as string];
			return rowValue === filterValue;
		});
	},
	[FilterTypes.NUMBER]: (rows, id, filterValue: Record<string, number>) =>
		rows.filter((row) => {
			let rowValue = row.values[id as string];
			if (typeof rowValue === "string" && rowValue.includes("/")) {
				rowValue = rowValue.split("/")[0];
			}

			const numValue = parseFloat(rowValue);
			if (isNaN(numValue)) {
				return false;
			}
			const hasGreaterThan = filterValue.hasOwnProperty(
				NumberOperators.greaterThan,
			);
			const hasLessThan = filterValue.hasOwnProperty(
				NumberOperators.lessThan,
			);
			const hasEqualTo = filterValue.hasOwnProperty(
				NumberOperators.equalTo,
			);

			if (hasGreaterThan && hasLessThan && !hasEqualTo)
				return (
					numValue > filterValue[NumberOperators.greaterThan] &&
					numValue < filterValue[NumberOperators.lessThan]
				);

			if (hasGreaterThan && hasEqualTo && !hasLessThan)
				return (
					numValue > filterValue[NumberOperators.greaterThan] ||
					numValue === filterValue[NumberOperators.equalTo]
				);

			if (hasLessThan && hasEqualTo && !hasGreaterThan)
				return (
					numValue < filterValue[NumberOperators.lessThan] ||
					numValue === filterValue[NumberOperators.equalTo]
				);

			if (hasEqualTo && !hasGreaterThan && !hasLessThan)
				return numValue === filterValue[NumberOperators.equalTo];

			if (hasGreaterThan && !hasLessThan && !hasEqualTo)
				return numValue > filterValue[NumberOperators.greaterThan];

			if (hasLessThan && !hasGreaterThan && !hasEqualTo)
				return numValue < filterValue[NumberOperators.lessThan];

			return false;
		}),
	[FilterTypes.ENUM]: (rows, id, filterValue: string | undefined) => {
		if (filterValue === undefined) return rows;
		const values = filterValue
			.split("||")
			.map((v) => v.replace(/\s/g, "").toLowerCase());
		return rows.filter((row) => {
			const rowVal = String(row.values[id as string] ?? "")
				.replace(/\s/g, "")
				.toLowerCase();
			return values.includes(rowVal);
		});
	},
	[FilterTypes.DATE]: dateFilter,
	[FilterTypes.DATE_EXPIRED]: dateFilter,
	[FilterTypes.USER]: (rows, id, filterValue: string) =>
		rows.filter((row) => {
			try {
				const rowValue = row.values[id as string] as UserDto;
				const formattedValue = formatUserNameWithEmailString(rowValue);
				return (
					dodgySearch(formattedValue, filterValue) ||
					filterValue === rowValue.id
				);
			} catch (e) {
				console.error(e);
				return false;
			}
		}),
});
